import { FC, useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet";
import Input from "shared/Input/Input";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { Link, useHistory } from "react-router-dom";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  CHECK_EMAIL_EXISTS_SIGNUP,
  SIGNUP,
  CHECK_ARTIST_NAME_EXISTS_SIGNUP,
} from "./signup_query";
import FormItem from "components/FormItem";
import { XCircleIcon } from "@heroicons/react/24/solid";
import { useForm } from "react-hook-form";
import ReactGA4 from "react-ga4";

export interface PageSignUpProps {
  className?: string;
}

interface SignUpFormData {
  name: string;
  artistName: string;
  email: string;
  password: string;
}

const PageSignUp: FC<PageSignUpProps> = ({ className = "" }) => {
  const history = useHistory();
  const [signupmutate, { data, loading, error }] = useMutation(SIGNUP);
  const [searchEmail, { data: checkEmailData, loading: checkingEmail }] =
    useLazyQuery(CHECK_EMAIL_EXISTS_SIGNUP);
  const [
    searchArtistName,
    { data: checkArtistNameData, loading: checkingArtistName },
  ] = useLazyQuery(CHECK_ARTIST_NAME_EXISTS_SIGNUP);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    watch,
  } = useForm<SignUpFormData>({
    mode: "onBlur",
  });

  const emailValue = watch("email");
  const artistNameValue = watch("artistName");
  const [emailChecked, setEmailChecked] = useState(false);
  const [artistNameChecked, setArtistNameChecked] = useState(false);

  useEffect(() => {
    /* Non-interaction event */
    ReactGA4.send({ hitType: "pageview", page: window.location.pathname });
  }, []);

  // Reset email checked state when email changes
  useEffect(() => {
    setEmailChecked(false);
    clearErrors("email");
  }, [emailValue, clearErrors]);

  // Reset artist name checked state when artistName changes
  useEffect(() => {
    setArtistNameChecked(false);
    clearErrors("artistName");
  }, [artistNameValue, clearErrors]);

  // Handle successful signup - move side effects to useEffect
  useEffect(() => {
    if (data?.signUp) {
      localStorage.setItem("token", data.signUp.token);
      localStorage.setItem("name", data.signUp.user.name);
      localStorage.setItem("user_id", data.signUp.user.id);
      localStorage.setItem("user_email", data.signUp.user.email);
      localStorage.setItem("user_avatar", data.signUp.user.avatarUrl);
      localStorage.setItem("artistName", data.signUp.user.artistName);
      localStorage.setItem("bio", data.signUp.user.bio);
      localStorage.setItem("follows_Count", data.signUp.user.followsCount);
      localStorage.setItem(
        "followedBy_Count",
        data.signUp.user.followedByCount
      );
      localStorage.setItem("projects_Count", data.signUp.user.projectsCount);
      localStorage.setItem("header_Image", data.signUp.user.headerImage);

      history.push("/");
    }
  }, [data, history]);

  // Check email when checkEmailData changes
  useEffect(() => {
    if (checkEmailData?.getUserByEmail && emailChecked) {
      setError("email", {
        type: "manual",
        message: "That email already exists, try a different one!",
      });
    } else if (!checkEmailData?.getUserByEmail && emailChecked) {
      clearErrors("email");
    }
  }, [checkEmailData, emailChecked, setError, clearErrors]);

  // Check artist name when checkArtistNameData changes
  useEffect(() => {
    if (checkArtistNameData?.getUserByArtistName && artistNameChecked) {
      setError("artistName", {
        type: "manual",
        message: "That artist name already exists, try a different one!",
      });
    } else if (!checkArtistNameData?.getUserByArtistName && artistNameChecked) {
      clearErrors("artistName");
    }
  }, [checkArtistNameData, artistNameChecked, setError, clearErrors]);

  // Async email validation function
  const validateEmail = useCallback(
    async (email: string): Promise<boolean | string> => {
      if (!email) return true; // Let required validation handle empty emails

      try {
        const result = await searchEmail({
          variables: { email },
        });
        setEmailChecked(true);

        if (result.data?.getUserByEmail) {
          return "That email already taken, try a different one!";
        }
        return true;
      } catch (err) {
        // If there's an error checking, allow submission (server will handle it)
        return true;
      }
    },
    [searchEmail]
  );

  // Async artist name validation function
  const validateArtistName = useCallback(
    async (artistName: string): Promise<boolean | string> => {
      if (!artistName) return true; // Let required validation handle empty artist names

      try {
        const result = await searchArtistName({
          variables: { artistName },
        });
        setArtistNameChecked(true);

        if (result.data?.getUserByArtistName) {
          return "That artist name already taken, try a different one!";
        }
        return true;
      } catch (err) {
        // If there's an error checking, allow submission (server will handle it)
        return true;
      }
    },
    [searchArtistName]
  );

  return (
    <div className={`nc-PageSignUp  ${className}`} data-nc-id="PageSignUp">
      <Helmet>
        <title>Sign up - Djaminn Web Player</title>
      </Helmet>
      <div className="container  font-Montserrat italic">
        <h2 className="my-5 flex items-center text-3xl leading-[115%] md:text-5xl md:leading-[115%] font-Montserrat italic text-neutral-900 dark:text-neutral-100 justify-center">
          Signup
        </h2>
        <div className="max-w-md mx-auto space-y-6 ">
          {/* FORM */}
          {error ? (
            <div className="flex items-center justify-start space-x-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6 text-red-600"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>

              <div className="font-thin text-xs opacity-70">
                <span className="bg-red-100 text-red-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded dark:bg-red-200 dark:text-red-800">
                  {error.message || "Error, while Creating Account!"}
                </span>
              </div>
            </div>
          ) : (
            ""
          )}
          <form
            className="grid grid-cols-1 gap-6"
            onSubmit={handleSubmit(async (formData) => {
              // Final email check before submission
              const emailValid = await validateEmail(formData.email);
              if (emailValid !== true) {
                return;
              }

              // Final artist name check before submission
              const artistNameValid = await validateArtistName(
                formData.artistName
              );
              if (artistNameValid !== true) {
                return;
              }

              // Proceed with signup
              signupmutate({
                variables: {
                  name: formData.name,
                  artistName: formData.artistName,
                  email: formData.email,
                  password: formData.password,
                },
              });
            })}
          >
            <label className="block">
              <span className="text-neutral-800 dark:text-neutral-200">
                Name
              </span>
              <Input
                {...register("name", {
                  required: "Name is required",
                  minLength: {
                    value: 3,
                    message: "Name must be at least 3 characters",
                  },
                  maxLength: {
                    value: 20,
                    message: "Name must not exceed 20 characters",
                  },
                })}
                type="text"
                placeholder="Name"
                className="mt-1 italic"
              />
            </label>

            {errors.name && (
              <span className="text-red-600 text-sm mt-1">
                {errors.name.message || "Name is required"}
              </span>
            )}
            <FormItem label="ArtistName" desc="">
              <div className="flex">
                <Input
                  {...register("artistName", {
                    required: "Artist name is required",
                    minLength: {
                      value: 3,
                      message: "Artist name must be at least 3 characters",
                    },
                    maxLength: {
                      value: 20,
                      message: "Artist name must not exceed 20 characters",
                    },
                    validate: validateArtistName,
                  })}
                  type="text"
                  placeholder="ArtistName"
                  className="mt-1 italic rounded"
                />
              </div>
              {errors.artistName && (
                <span className="inline-flex items-center mt-2 justify-start w-full">
                  <XCircleIcon className="h-5 w-5 text-red-600 mr-2" />
                  <p className="text-sm text-red-600">
                    {errors.artistName.message}
                  </p>
                </span>
              )}
              {checkingArtistName && artistNameValue && (
                <p className="text-sm text-blue-600 mt-2">
                  Checking artist name availability...
                </p>
              )}
            </FormItem>

            <FormItem label="Email address" desc="">
              <div className="flex">
                <Input
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                    validate: validateEmail,
                  })}
                  type="email"
                  placeholder="example@example.com"
                  className="rounded italic"
                />
              </div>
              {errors.email && (
                <span className="inline-flex items-center mt-2 justify-start w-full">
                  <XCircleIcon className="h-5 w-5 text-red-600 mr-2" />
                  <p className="text-sm text-red-600">{errors.email.message}</p>
                </span>
              )}
              {checkingEmail && emailValue && (
                <p className="text-sm text-blue-600 mt-2">
                  Checking email availability...
                </p>
              )}
            </FormItem>
            <FormItem label="Password" desc="">
              <div className="flex">
                <Input
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 6,
                      message: "Password must be at least 6 characters",
                    },
                  })}
                  type="password"
                  placeholder="type your password"
                  className="rounded italic"
                />
              </div>
              {errors.password && (
                <p className="text-red-600 text-sm mt-1">
                  {errors.password.message || "Password is required"}
                </p>
              )}
            </FormItem>
            <ButtonPrimary
              type="submit"
              className="bg-pink-500 shadow-lg hover:bg-pink-400 italic"
            >
              {loading ? (
                <>
                  <svg
                    className={`motion-safe:animate-spin dark:text-white text-black  mr-3 h-5 w-5`}
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <div className="font-thin text-xs opacity-70">
                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded dark:bg-blue-200 dark:text-blue-800">
                      Please Wait, Creating Account!
                    </span>
                  </div>
                </>
              ) : (
                "Continue"
              )}
            </ButtonPrimary>
          </form>

          {/* ==== */}
          <span className="block text-center text-neutral-700 dark:text-neutral-300">
            Already have an Account? {` `}
            <Link className="text-pink-500 hover:text-pink-400" to="/login">
              Sign in
            </Link>
          </span>
        </div>
      </div>
    </div>
  );
};

export default PageSignUp;
